import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Calendar, CreditCard, Edit, DollarSign, MapPin, Phone, Briefcase, FileText, CheckCircle, XCircle, Clock, AlertTriangle, CalendarClock, ArrowLeft, Home, Camera, Upload, History, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import DoctorScheduleManager from '../components/DoctorScheduleManager';
import UserAvatar from '../components/UserAvatar';

// Liste des spécialisations médicales
const SPECIALIZATIONS = [
  "Allergologie",
  "Anesthésiologie",
  "Cardiologie",
  "Chirurgie Générale",
  "Chirurgie Orthopédique",
  "Chirurgie Plastique",
  "Dermatologie",
  "Endocrinologie",
  "Gastro-entérologie",
  "Gériatrie",
  "Gynécologie",
  "Hématologie",
  "Infectiologie",
  "Médecine Générale",
  "Médecine Interne",
  "Néphrologie",
  "Neurologie",
  "Oncologie",
  "Ophtalmologie",
  "ORL (Oto-Rhino-Laryngologie)",
  "Pédiatrie",
  "Pneumologie",
  "Psychiatrie",
  "Radiologie",
  "Rhumatologie",
  "Urologie"
];

// Liste des villes d'Abidjan
const CITIES = [
  "Abobo",
  "Adjamé",
  "Attécoubé",
  "Cocody",
  "Koumassi",
  "Marcory",
  "Plateau",
  "Port-Bouet",
  "Treichville",
  "Yopougon"
];

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [expandedPatientHistory, setExpandedPatientHistory] = useState({});
  const [formData, setFormData] = useState({
    specialization: '',
    experience_years: '',
    clinic_name: '',
    clinic_address: '',
    city: '',
    phone: '',
    bio: '',
    consultation_fee: '',
    photo_url: '',
    location: { lat: 0, lng: 0 }
  });

  // Helper function to get past appointments for a specific patient
  const getPatientHistory = (patientId, currentAppointmentId) => {
    const now = new Date();
    return appointments.filter(apt => 
      apt.patient_id === patientId && 
      apt.id !== currentAppointmentId &&
      (new Date(apt.appointment_date) < now || ['completed', 'cancelled', 'cancelled_by_doctor', 'cancelled_by_patient'].includes(apt.status))
    ).sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
  };

  // Toggle patient history visibility
  const togglePatientHistory = (appointmentId) => {
    setExpandedPatientHistory(prev => ({
      ...prev,
      [appointmentId]: !prev[appointmentId]
    }));
  };

  useEffect(() => {
    checkSubscriptionAndFetchData();
  }, []);

  const checkSubscriptionAndFetchData = async () => {
    try {
      // First check subscription info
      const subResponse = await axios.get(`${API}/doctors/subscription-info`);
      setSubscriptionInfo(subResponse.data);
      
      // If subscription expired, redirect to payment page
      if (subResponse.data.has_profile && subResponse.data.subscription_status === 'expired') {
        navigate('/payment-required');
        return;
      }
      
      // If has profile, fetch it
      if (subResponse.data.has_profile) {
        await fetchProfile();
      } else {
        setEditMode(true);
      }
      
      await fetchAppointments();
    } catch (error) {
      console.error('Error checking subscription', error);
      // If no profile, show edit mode
      setEditMode(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/doctors/profile/me`);
      setProfile(response.data);
      setFormData({
        specialization: response.data.specialization,
        experience_years: response.data.experience_years,
        clinic_name: response.data.clinic_name,
        clinic_address: response.data.clinic_address,
        city: response.data.city,
        phone: response.data.phone,
        bio: response.data.bio || '',
        photo_url: response.data.photo_url || '',
        consultation_fee: response.data.consultation_fee,
        location: response.data.location
      });
      
      // Check if trial expired
      if (response.data.subscription_status === 'expired') {
        navigate('/payment-required');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setEditMode(true);
      }
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 5 Mo.');
      return;
    }

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/doctors/upload-photo`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setFormData(prev => ({ ...prev, photo_url: response.data.photo_url }));
      if (profile) {
        setProfile(prev => ({ ...prev, photo_url: response.data.photo_url }));
      }
      toast.success('Photo uploadée avec succès!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      if (profile) {
        await axios.put(`${API}/doctors/profile`, formData);
        toast.success('Profil mis à jour avec succès');
      } else {
        await axios.post(`${API}/doctors/profile`, formData);
        toast.success('Profil créé avec succès');
      }
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'enregistrement du profil');
    }
  };

  const handleSubscribe = async () => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout`, { origin_url: originUrl });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'initialisation du paiement');
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      // Map 'cancelled' to 'cancelled_by_doctor' when doctor cancels
      const finalStatus = status === 'cancelled' ? 'cancelled_by_doctor' : status;
      await axios.put(`${API}/appointments/${appointmentId}/status?status=${finalStatus}`);
      const statusText = status === 'confirmed' ? 'confirmé' : status === 'cancelled' ? 'annulé' : 'terminé';
      toast.success(`Rendez-vous ${statusText}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Échec de la mise à jour du rendez-vous');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="doctor-dashboard">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-1 text-gray-500 hover:text-teal-600 transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                <Home className="w-4 h-4" />
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-teal-700">BabiDoc - Portail Médecin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                data-testid="profile-link"
              >
                <UserAvatar name={user?.full_name} role={user?.role} size="sm" />
                <span className="font-medium hidden sm:inline">{user?.full_name}</span>
              </button>
              <Button 
                variant="ghost" 
                onClick={handleLogout} 
                data-testid="logout-btn"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5 mr-1" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Status */}
        {profile && subscriptionInfo && subscriptionInfo.subscription_status === 'trial' && (
          <Card className="card mb-8 bg-gradient-to-r from-blue-50 to-teal-50 border-blue-200" data-testid="trial-status">
            <CardContent className="py-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-3">
                  <Clock className="w-8 h-8 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900">Période d'Essai Gratuite</h3>
                    <p className="text-blue-700">
                      Il vous reste <strong>{subscriptionInfo.days_remaining} jours</strong> d'essai gratuit
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-700 mb-2">
                    Expire le: {new Date(subscriptionInfo.trial_end).toLocaleDateString('fr-FR')}
                  </p>
                  <Button onClick={handleSubscribe} className="btn-primary" data-testid="subscribe-early-btn">
                    <CreditCard className="w-5 h-5 mr-2" />
                    S'abonner maintenant (20 000 FCFA/mois)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Subscription Status */}
        {profile && subscriptionInfo && subscriptionInfo.subscription_status === 'active' && (
          <Card className="card mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="subscription-active">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-900">Abonnement Actif</h3>
                    <p className="text-emerald-700">Votre profil est visible aux patients</p>
                  </div>
                </div>
                {subscriptionInfo.subscription_expires && (
                  <p className="text-sm text-emerald-700">
                    Expire le: {new Date(subscriptionInfo.subscription_expires).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-8 flex-wrap">
            <TabsTrigger value="profile" data-testid="profile-tab">Mon Profil</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="schedule-tab">
              <CalendarClock className="w-4 h-4 mr-2" />
              Disponibilités
            </TabsTrigger>
            <TabsTrigger value="appointments" data-testid="appointments-tab" className="relative">
              Rendez-vous
              {(() => {
                const now = new Date();
                const pendingFutureCount = appointments.filter(apt => 
                  apt.status === 'pending' && new Date(apt.appointment_date) > now
                ).length;
                return pendingFutureCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingFutureCount}
                  </span>
                );
              })()}
            </TabsTrigger>
            <TabsTrigger value="past-appointments" data-testid="past-appointments-tab">
              <History className="w-4 h-4 mr-2" />
              Historique
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedule">
            <Card className="card">
              <CardContent className="pt-6">
                <DoctorScheduleManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            <Card className="card">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Profil du Médecin</CardTitle>
                  {profile && !editMode && (
                    <Button onClick={() => setEditMode(true)} variant="outline" data-testid="edit-profile-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier le Profil
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-6" data-testid="profile-form">
                    {/* Photo Upload Section */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-teal-500 shadow-lg">
                          {formData.photo_url ? (
                            <img 
                              src={formData.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${formData.photo_url}` : formData.photo_url} 
                              alt="Photo de profil" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200">
                              <User className="w-16 h-16 text-teal-600" />
                            </div>
                          )}
                        </div>
                        <label 
                          htmlFor="photo-upload" 
                          className="absolute bottom-0 right-0 bg-teal-600 hover:bg-teal-700 text-white p-2 rounded-full cursor-pointer shadow-lg transition-colors"
                        >
                          {uploadingPhoto ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Camera className="w-5 h-5" />
                          )}
                        </label>
                        <input
                          id="photo-upload"
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploadingPhoto}
                          data-testid="photo-upload-input"
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Cliquez sur l'icône pour ajouter une photo</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Spécialisation</label>
                        <Select 
                          value={formData.specialization} 
                          onValueChange={(value) => setFormData({ ...formData, specialization: value })}
                        >
                          <SelectTrigger data-testid="specialization-select">
                            <SelectValue placeholder="Choisir une spécialisation" />
                          </SelectTrigger>
                          <SelectContent>
                            {SPECIALIZATIONS.map((spec) => (
                              <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Expérience (années)</label>
                        <Input
                          type="number"
                          value={formData.experience_years}
                          onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) })}
                          data-testid="experience-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la Clinique</label>
                        <Input
                          value={formData.clinic_name}
                          onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })}
                          data-testid="clinic-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                        <Select 
                          value={formData.city} 
                          onValueChange={(value) => setFormData({ ...formData, city: value })}
                        >
                          <SelectTrigger data-testid="city-select">
                            <SelectValue placeholder="Choisir une ville" />
                          </SelectTrigger>
                          <SelectContent>
                            {CITIES.map((city) => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          data-testid="phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Frais de Consultation (FCFA)</label>
                        <Input
                          type="number"
                          step="100"
                          placeholder="Ex: 15000"
                          value={formData.consultation_fee}
                          onChange={(e) => setFormData({ ...formData, consultation_fee: parseFloat(e.target.value) })}
                          data-testid="fee-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse de la Clinique</label>
                      <Input
                        value={formData.clinic_address}
                        onChange={(e) => setFormData({ ...formData, clinic_address: e.target.value })}
                        data-testid="address-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Biographie</label>
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        placeholder="Parlez de vous aux patients..."
                        rows={4}
                        data-testid="bio-input"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={formData.location.lat}
                          onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lat: parseFloat(e.target.value) } })}
                          data-testid="lat-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                        <Input
                          type="number"
                          step="0.000001"
                          value={formData.location.lng}
                          onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lng: parseFloat(e.target.value) } })}
                          data-testid="lng-input"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button onClick={handleSaveProfile} className="btn-primary" data-testid="save-profile-btn">
                        Enregistrer le Profil
                      </Button>
                      {profile && (
                        <Button onClick={() => setEditMode(false)} variant="outline" data-testid="cancel-edit-btn">
                          Annuler
                        </Button>
                      )}
                    </div>
                  </div>
                ) : profile ? (
                  <div className="space-y-6" data-testid="profile-view">
                    {/* Photo display */}
                    <div className="flex justify-center mb-6">
                      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-teal-500 shadow-lg">
                        {profile.photo_url ? (
                          <img 
                            src={profile.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${profile.photo_url}` : profile.photo_url} 
                            alt="Photo de profil" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200">
                            <User className="w-16 h-16 text-teal-600" />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="flex items-start space-x-3">
                        <Briefcase className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Spécialisation</p>
                          <p className="font-semibold">{profile.specialization}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Calendar className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Expérience</p>
                          <p className="font-semibold">{profile.experience_years} ans</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <FileText className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Nom de la Clinique</p>
                          <p className="font-semibold">{profile.clinic_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Ville</p>
                          <p className="font-semibold">{profile.city}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Phone className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Téléphone</p>
                          <p className="font-semibold">{profile.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <DollarSign className="w-5 h-5 text-teal-600 mt-1" />
                        <div>
                          <p className="text-sm text-gray-600">Frais de Consultation</p>
                          <p className="font-semibold">{profile.consultation_fee?.toLocaleString()} FCFA</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Adresse de la Clinique</p>
                      <p className="font-semibold">{profile.clinic_address}</p>
                    </div>
                    {profile.bio && (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Biographie</p>
                        <p className="text-gray-700">{profile.bio}</p>
                      </div>
                    )}
                    {profile.rating > 0 && (
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-gray-600">Note:</p>
                        <div className="flex items-center space-x-1">
                          <span className="font-semibold text-lg">{profile.rating}</span>
                          <span className="text-gray-600">({profile.total_reviews} avis)</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-600 py-8">Veuillez créer votre profil pour commencer</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="appointments">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Rendez-vous à traiter</h3>
            {(() => {
              const now = new Date();
              const activeAppointments = appointments.filter(apt => 
                new Date(apt.appointment_date) >= now && 
                !['completed', 'cancelled', 'cancelled_by_doctor', 'cancelled_by_patient'].includes(apt.status)
              );
              return activeAppointments.length === 0 ? (
                <Card className="card">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucun rendez-vous à traiter</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="appointments-list">
                  {activeAppointments.map((appointment) => {
                    const patientHistory = getPatientHistory(appointment.patient_id, appointment.id);
                    const isHistoryExpanded = expandedPatientHistory[appointment.id];
                    
                    return (
                      <Card key={appointment.id} className="card" data-testid={`appointment-${appointment.id}`}>
                        <CardContent className="py-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-3">
                                <h3 className="text-lg font-semibold">{appointment.patient_name}</h3>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  appointment.status === 'cancelled_by_doctor' ? 'bg-red-100 text-red-800' :
                                  appointment.status === 'cancelled_by_patient' ? 'bg-orange-100 text-orange-800' :
                                  appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {appointment.status === 'pending' && 'En attente'}
                                  {appointment.status === 'confirmed' && 'Confirmé'}
                                  {appointment.status === 'cancelled' && 'Annulé'}
                                  {appointment.status === 'cancelled_by_doctor' && 'Annulé par vous'}
                                  {appointment.status === 'cancelled_by_patient' && 'Annulé par le patient'}
                                  {appointment.status === 'completed' && 'Terminé'}
                                </span>
                                {patientHistory.length > 0 && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Patient connu ({patientHistory.length} visite{patientHistory.length > 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                              <div className="space-y-2 text-sm text-gray-600">
                                <div className="flex items-center space-x-2">
                                  <Calendar className="w-4 h-4" />
                                  <span>{new Date(appointment.appointment_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4" />
                                  <span>{appointment.time_slot || new Date(appointment.appointment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                <div className="mt-2">
                                  <p className="font-medium text-gray-700">Raison:</p>
                                  <p>{appointment.reason}</p>
                                </div>
                              </div>
                              
                              {/* Patient History Section */}
                              {patientHistory.length > 0 && (
                                <div className="mt-4 border-t pt-4">
                                  <button
                                    onClick={() => togglePatientHistory(appointment.id)}
                                    className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    data-testid={`toggle-history-${appointment.id}`}
                                  >
                                    <History className="w-4 h-4" />
                                    <span>Historique avec ce patient</span>
                                    {isHistoryExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                  
                                  {isHistoryExpanded && (
                                    <div className="mt-3 space-y-2 pl-4 border-l-2 border-blue-200">
                                      {patientHistory.map((pastApt) => (
                                        <div key={pastApt.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-gray-700">
                                              {new Date(pastApt.appointment_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                                              pastApt.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                              pastApt.status === 'cancelled_by_doctor' || pastApt.status === 'cancelled_by_patient' || pastApt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                              'bg-gray-100 text-gray-700'
                                            }`}>
                                              {pastApt.status === 'completed' && 'Terminé'}
                                              {pastApt.status === 'cancelled' && 'Annulé'}
                                              {pastApt.status === 'cancelled_by_doctor' && 'Annulé par vous'}
                                              {pastApt.status === 'cancelled_by_patient' && 'Annulé par patient'}
                                            </span>
                                          </div>
                                          {pastApt.reason && (
                                            <p className="text-gray-600 text-xs italic">Motif: {pastApt.reason}</p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              {appointment.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`confirm-${appointment.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Confirmer
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                                    data-testid={`cancel-${appointment.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Refuser
                                  </Button>
                                </>
                              )}
                              {appointment.status === 'confirmed' && (
                                <Button
                                  size="sm"
                                  onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                                  data-testid={`complete-${appointment.id}`}
                                >
                                  Marquer Terminé
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="past-appointments">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Historique des rendez-vous</h3>
            {(() => {
              const now = new Date();
              const pastAppointments = appointments.filter(apt => 
                new Date(apt.appointment_date) < now || 
                ['completed', 'cancelled', 'cancelled_by_doctor', 'cancelled_by_patient'].includes(apt.status)
              ).sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
              
              return pastAppointments.length === 0 ? (
                <Card className="card">
                  <CardContent className="py-12 text-center">
                    <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucun rendez-vous passé</p>
                    <p className="text-sm text-gray-400 mt-2">Votre historique de consultations apparaîtra ici</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="past-appointments-list">
                  {pastAppointments.map((appointment) => (
                    <Card key={appointment.id} className="card bg-gray-50" data-testid={`past-appointment-${appointment.id}`}>
                      <CardContent className="py-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-3">
                              <h3 className="text-lg font-semibold text-gray-700">{appointment.patient_name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                appointment.status === 'cancelled_by_doctor' ? 'bg-red-100 text-red-800' :
                                appointment.status === 'cancelled_by_patient' ? 'bg-orange-100 text-orange-800' :
                                appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {appointment.status === 'completed' && 'Terminé'}
                                {appointment.status === 'cancelled' && 'Annulé'}
                                {appointment.status === 'cancelled_by_doctor' && 'Annulé par vous'}
                                {appointment.status === 'cancelled_by_patient' && 'Annulé par le patient'}
                                {appointment.status === 'pending' && 'En attente (passé)'}
                                {appointment.status === 'confirmed' && 'Confirmé (passé)'}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>{new Date(appointment.appointment_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>{appointment.time_slot || new Date(appointment.appointment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              {appointment.reason && (
                                <div className="mt-2">
                                  <p className="font-medium text-gray-600">Motif:</p>
                                  <p className="text-gray-500 italic">{appointment.reason}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DoctorDashboard;